import React, { Fragment, FunctionComponent, useMemo } from 'react'

import { FieldArray as FormikFieldArray } from 'formik'

import { Button, ButtonSize, ButtonVariant } from '@/atoms/button'
import { Box } from '@/atoms/layout'
import { Legend } from '@/atoms/typography'
import { FieldWrapper, FormControl } from '@/components/form-elements'
import { arrayToInitialValues } from '@/helpers/table'
import { isEmpty } from '@/helpers/type-guards'
import { getFieldCount } from '@/helpers/utils'
import { LoanBookType } from '@/models/back-office-pages/loan-book'
import { FundingLine } from '@/models/back-office-pages/prospects'
import { Currency, Field } from '@/models/common'
import { Space } from '@/styles'

export interface ValueType {
  [field: string]:
    | string
    | string[]
    | number
    | boolean
    | null
    | Date
    | Array<{
        [field: string]: string | number | boolean | Date | Array<{ [field: string]: string | number | boolean | Date }>
      }>
    | { [field: string]: string | number | boolean | Date | Array<{ [field: string]: string | number | boolean | Date }> }
    | FundingLine
    | Currency
    | LoanBookType
}

type Fields = ((index: number) => Array<Field>) | Array<Field> | ((index: number) => React.JSX.Element)

export interface FieldArrayProps {
  name?: string
  values?: Array<ValueType>
  fields?: Fields
  addButtonText?: string
  removeButtonText?: string
  fieldArrayTitle?: string
  initialArrayValues?: ValueType
  customComponent?: boolean
  tooltip?: string
  allowEmpty?: boolean
}

const FieldArray: FunctionComponent<FieldArrayProps> = ({
  name,
  values,
  fields,
  addButtonText,
  removeButtonText,
  fieldArrayTitle,
  initialArrayValues,
  customComponent,
  tooltip,
  allowEmpty,
}: FieldArrayProps) => {
  const getFields = (index?: number): Array<Field> | React.JSX.Element => {
    if (typeof fields === 'function') {
      return fields(index)
    }
    return fields
  }

  const initialValues =
    initialArrayValues || (!customComponent && arrayToInitialValues((getFields() as Array<Field>).map(({ name: fieldName }) => fieldName)))
  const fieldArray = useMemo(
    () => (isEmpty(values) && !allowEmpty ? [{ ...initialValues }] : (values ?? [])),
    [values, initialValues, allowEmpty],
  )
  const fieldArrayLength = fieldArray.length
  const hasOneElementInArray = fieldArrayLength === 1

  return (
    <FormikFieldArray
      name={name}
      render={arrayHelpers => (
        <Fragment>
          {fieldArray.map((val, index) => (
            <Fragment key={`${name}-${index}`}>
              {fieldArrayTitle && (
                <Legend mb={Space.S12} px={Space.S12} width={1}>
                  {fieldArrayTitle} {index + 1}
                </Legend>
              )}
              {customComponent ? (
                <FieldWrapper>{getFields(index) as React.JSX.Element}</FieldWrapper>
              ) : (
                (getFields(index) as Array<Field>).map(
                  ({ label, control, name: fieldName, width, hideField, formatValue, decimalScale, valueFormat, ...rest }, fieldIndex) => {
                    if (hideField) return null

                    const value = formatValue ? formatValue(val[fieldName]) : val[fieldName]
                    const formattedFieldLabel = fieldArrayTitle ? label : `${label} ${getFieldCount(index, values.length)}`

                    return (
                      <FieldWrapper key={`${label}-${fieldIndex}`} width={width} mb={Space.S12}>
                        <FormControl
                          label={label ? formattedFieldLabel : undefined}
                          name={`${name}[${index}].${fieldName}`}
                          control={control}
                          valueFormat={valueFormat}
                          fieldValue={value}
                          decimalScale={decimalScale}
                          tooltip={tooltip}
                          {...rest}
                        />
                      </FieldWrapper>
                    )
                  },
                )
              )}
              {(allowEmpty || !hasOneElementInArray) && removeButtonText && (
                <Box width={1} px={Space.S12} mb={Space.S12}>
                  <Button onClick={() => arrayHelpers.remove(index)} variant={ButtonVariant.INVERTED_FLAT_ERROR} size={ButtonSize.SMALL}>
                    {removeButtonText}
                  </Button>
                </Box>
              )}
            </Fragment>
          ))}
          {addButtonText && (
            <Box width={1} px={Space.S12} mb={Space.S12}>
              <Button onClick={() => arrayHelpers.push(initialValues)} variant={ButtonVariant.FLAT} size={ButtonSize.SMALL}>
                {addButtonText}
              </Button>
            </Box>
          )}
        </Fragment>
      )}
    />
  )
}

export default FieldArray
